import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@src/common/Button';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useTinRegistration } from '@src/state/tinRegistration';
import { storage } from '@uhg-abyss/web/tools/storage';
import { config } from '@uhg-abyss/web/tools/config';
import { useForm as useAbyssForm } from '@uhg-abyss/web/hooks/useForm';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useUser } from '@src/state/user/hooks';
import { userAgent } from '@uhg-abyss/web/tools/userAgent';
import { uniqueId } from 'lodash';

const SignInput = styled('div', {
  display: 'inline-flex',
  marginBottom: '20px',
  marginRight: '10px',
});

const SignatureText = styled('div', {
  display: 'inline-flex',
});

const UploadingMessage = styled('div', {
  marginTop: '-1px',
  fontSize: '13px',
  color: '$accent1',
});

const UploadSuccessMessage = styled('div', {
  marginTop: '-1px',
  fontSize: '13px',
  color: '$success1',
});

const UploadErrorMessage = styled('div', {
  marginTop: '-16px',
  fontSize: '13px',
  color: '$error1',
});

const SIGNATURE_TYPE = 'signature-type';

export const TextSignature = (props) => {
  const abyssForm = useAbyssForm();
  const textSignature = abyssForm.getValues('attestation.textSignature');
  const { uploadProof, setDocumentId, uploadSignature } =
    useTinRegistration.actions();

  const { uploadProofSaga } = useTinRegistration.state();
  const registration = abyssForm.getValues('searchRegistration');
  const { trackingNumber } = useTinRegistration.state();
  const { model, className, getImage, x, y, height, font, required } = props;

  const [imgBase64, setImgBase64] = useState('');
  const [signText, setSignText] = useState(' ');

  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFail, setUploadFail] = useState(false);
  const [disableCreateSign, setDisableCreateSign] = useState(false);

  const { documentIds } = useTinRegistration.state();
  const userInfo = useUser.state();
  const { isCheckoutUser } = userInfo;

  const getBinaryFile = (img) => {
    const blobBin = atob(img.split(',')[1]);
    const array = [];
    for (let i = 0; i < blobBin.length; i++) {
      array.push(blobBin.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], {
      type: 'image/png',
      encoding: 'utf-8',
    });
  };

  const generateBase64Img = (img, blobFile) => {
    storage.local.set(SIGNATURE_TYPE, img);
    setImgBase64(img);
    getImage(img);
  };

  const loadCanvasImage = (text) => {
    const canvasTxt = document
      .getElementById('canvasComponent')
      .getContext('2d');
    canvasTxt.canvas.width = canvasTxt.measureText(text).width;
    canvasTxt.canvas.height = height;
    canvasTxt.font = font;

    if (text && text.length > 0) {
      canvasTxt.fillText(text.substring(0, 31), x, y);
      canvasTxt.fillText(` ${text.substring(31, 61)}`, 0, 80);
    } else {
      canvasTxt.fillText(text, x, y);
    }
    const blobb = getBinaryFile(canvasTxt.canvas.toDataURL('image/png'));
    generateBase64Img(canvasTxt.canvas.toDataURL('image/png'), blobb);
  };

  const generateImage = (sign) => {
    const ctx = document.getElementById('canvasComponent').getContext('2d');
    ctx.canvas.width = ctx.measureText(sign).width;
    ctx.canvas.height = height;
    ctx.font = font;
    if (sign && sign.length > 0) {
      ctx.fillText(sign.substring(0, 31), x, y);
      ctx.fillText(` ${sign.substring(31, 61)}`, 0, 80);
    } else {
      ctx.fillText(sign, x, y);
    }
    storage.local.set(SIGNATURE_TYPE, ctx.canvas.toDataURL('image/png'));
    return getBinaryFile(ctx.canvas.toDataURL('image/png'));
  };

  const handleUploadResponse = (resp) => {
    if (resp && resp.doc360Id) {
      setUploadSuccess(true);
    } else {
      setUploadFail(true);
    }
  };

  const handleSaveTextSign = () => {
    const isSignEmpty = /^\s*$/.test(textSignature);
    if (
      textSignature !== null &&
      textSignature !== '' &&
      textSignature.length >= 2 &&
      !isSignEmpty
    ) {
      abyssForm.setValue('attestation.textSignatureCanvas', 'save-signature', {
        errors: {
          required: false,
        },
      });
      storage.local.remove(SIGNATURE_TYPE);
      setSignText(` ${textSignature}`);
      const imageBlob = generateImage(` ${textSignature}`);
      const tNum = trackingNumber;
      let file;
      if (!userAgent.isIE) {
        file = new File([imageBlob], `Signature-${tNum}.png`, {
          type: 'image/png',
        });
      }
      const reqBody = {
        billingTin: registration.billingTin,
        requestId: registration.requestId,
        fileType: 'US',
        trackingNumber: tNum,
        file: userAgent.isIE ? imageBlob : file,
        test: isCheckoutUser,
      };

      if (userAgent.isIE) {
        uploadSignature(
          reqBody,
          (resp) => {
            setDisableCreateSign(true);
            setDocumentId({ id: 'textSignature', value: resp.doc360Id });
            if (config('US3830334_SIGNATURE_FIX')) {
              handleUploadResponse(resp);
            } else {
              setUploadSuccess(true);
            }
          },
          (error) => {
            setUploadFail(true);
          }
        );
      } else {
        uploadProof(
          reqBody,
          (resp) => {
            setDisableCreateSign(true);
            setDocumentId({ id: 'textSignature', value: resp.doc360Id });
            if (config('US3830334_SIGNATURE_FIX')) {
              handleUploadResponse(resp);
            } else {
              setUploadSuccess(true);
            }
          },
          (error) => {
            setUploadFail(true);
          }
        );
      }
    } else {
      abyssForm.validate('attestation.textSignature');
    }
  };

  const handleClear = () => {
    setDisableCreateSign(false);
    storage.local.remove(SIGNATURE_TYPE);
    setUploadFail(false);
    setUploadSuccess(false);
    setSignText(` `);
    const interactionName = 'attestation widget';
    const interactionValue = 'clear signature';
    const eventInteraction = 'interaction';
    const interactionContext = 'type';
    adobeTaggingEvent('CLEAR_SIGNATURE', {
      eventInteraction,
      interactionName,
      interactionValue,
      interactionContext,
    });
    abyssForm.setValue('attestation.textSignature', '');
    abyssForm.setValue('attestation.textSignatureCanvas', '');
  };

  useEffect(() => {
    setDisableCreateSign(false);
  }, [textSignature]);

  useEffect(() => {
    loadCanvasImage(signText);
  }, [signText]);

  useEffect(() => {
    if (textSignature && documentIds.textSignature) {
      setSignText(` ${textSignature}`);
    }
  }, []);

  useEffect(() => {
    if (textSignature === '' || textSignature === null) {
      abyssForm.setValue('attestation.textSignatureCanvas', undefined, {
        errors: {
          required: true,
        },
      });
    }
  }, []);

  return (
    <React.Fragment>
      <div>
        <SignatureText>
          <SignInput>
            <React.Fragment>
              <TextInput
                id="attestation-text-signature-input"
                model="attestation.textSignature"
                label="Full Name"
                type="text"
                maxLength={60}
                width="380px"
                formatter={(value) => {
                  return value.replace(
                    /[-!@#$%^&*)({}<>,.[\]+_-`~=\\|';":/?0-9]/g,
                    ''
                  );
                }}
                validators={{
                  required: required ? 'Signature is required.' : '',
                  invalid: {
                    value: (value) => {
                      return !value.match(/[[a-zA-Z\]]/g) || value.length < 2;
                    },
                    message: 'Signature is invalid',
                  },
                }}
                name={`${model}-input-${uniqueId()}`}
                autoComplete="none"
              />
            </React.Fragment>
          </SignInput>
        </SignatureText>
        <React.Fragment>
          <div
            className={className}
            style={{
              border: '1px solid ',
              width: '80%',
              height: '100px',
              borderRadius: '3px',
            }}
          >
            <canvas id="canvasComponent" style={{ position: 'fixed' }} />
            <img
              id="imageComponent"
              style={{ maxWidth: 'none' }}
              src={imgBase64}
              alt=""
            />
          </div>
        </React.Fragment>
        <div style={{ marginBottom: '3%' }}>
          <Button
            variant="tertiary"
            style={{ marginTop: '10px' }}
            onClick={handleSaveTextSign}
            isDisabled={disableCreateSign}
          >
            Save Signature
          </Button>

          <Button
            variant="tertiary"
            style={{ marginTop: '10px', marginLeft: '14px' }}
            onClick={handleClear}
          >
            Clear
          </Button>
        </div>
        {abyssForm.getValues('attestation.textSignature') &&
          !abyssForm.getValues('attestation.textSignatureCanvas') && (
            <UploadErrorMessage id="saving-signature-upload-failed-message">
              Save signature before submitting
            </UploadErrorMessage>
          )}
        {uploadProofSaga.isLoading && (
          <UploadingMessage id="saving-signature-message">
            Saving signature, please wait...
          </UploadingMessage>
        )}
        {uploadSuccess && (
          <UploadSuccessMessage id="saving-signature-upload-success-message">
            Signature has been saved successfully.
          </UploadSuccessMessage>
        )}
        {uploadFail && (
          <UploadErrorMessage id="saving-signature-upload-failed-message">
            Failed to save signature, please clear and try again.
          </UploadErrorMessage>
        )}
      </div>
    </React.Fragment>
  );
};
TextSignature.propTypes = {
  className: PropTypes.string,
  getImage: PropTypes.func,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  font: PropTypes.string.isRequired,
  required: PropTypes.bool,
  model: PropTypes.string,
  validators: PropTypes.shape({}),
};

TextSignature.defaultProps = {
  className: null,
  getImage: () => {},
  required: false,
  model: '',
  validators: {},
};
